import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Form } from "@arcadeai/design-system/rhf";
import { KeyRound, Loader2, Play } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { usePlaygroundStore } from "@/app/playground/execute/hooks/use-store";
import { checkNeedsOAuthAuthorization } from "@/hooks/use-tool-requirements-status";
import { ParameterInput } from "./parameter-input";

type ToolFormProps = {
  form: UseFormReturn<Record<string, unknown>>;
  onSubmit: () => void;
  isExecuteDisabled: boolean;
  isExecuting: boolean;
  tooltipMessage: string | null;
};

export function ToolForm({
  form,
  onSubmit,
  isExecuteDisabled,
  isExecuting,
  tooltipMessage,
}: ToolFormProps) {
  const { selectedTool, parameters, authStatus } = usePlaygroundStore();
  const needsAuthorization = checkNeedsOAuthAuthorization(
    selectedTool?.requirements?.authorization,
    authStatus === "failed" ? "error" : (authStatus ?? undefined)
  );

  if (!selectedTool) {
    return null;
  }

  let Icon = Play;
  let label = "Run Tool";

  if (isExecuting) {
    Icon = Loader2;
    label = "Running...";
  } else if (needsAuthorization) {
    Icon = KeyRound;
    label = "Authorize & Run";
  }

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
      <Form {...form}>
        <form
          className="flex flex-col gap-5"
          id="tool-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isExecuteDisabled) {
              onSubmit();
            }
          }}
        >
          {parameters.map((parameter, index) => (
            <ParameterInput
              autoFocus={index === 0}
              form={form}
              key={`${parameter.name}-${parameter.description}`}
              parameter={parameter}
            />
          ))}

          {/* Execute Button */}
          <span
            className={cn(isExecuteDisabled && "cursor-not-allowed")}
            title={tooltipMessage ?? undefined}
          >
            <Button
              className="w-full gap-2"
              disabled={isExecuteDisabled}
              size="lg"
              type="submit"
            >
              <Icon className={cn("size-4", isExecuting && "animate-spin")} />
              {label}
            </Button>
          </span>
        </form>
      </Form>
    </div>
  );
}
